import { useEffect, useMemo, useState } from "react"
import { createDebouncer } from "../../../../features/dataFunctions/dataFunctions"
import { useLazyGetSearchResultsQuery } from "../../../../api/rtk/geocoder/slices/geocoderApiSlice"
import { store } from "../../../../stateManagement/store"
import { getItemWebStorage } from "../../../../features/webStorageSaver"
import { formatExtent } from "../../../map/mapFeatures/helpers"
import { containsCoordinate } from "ol/extent"
import { fromLonLat } from "ol/proj"
import { filterPathsByQuery, parseCoordinatesResult } from "../utils/searchUtils"

export function useMapSearchLogic() {
    const debouncer = useMemo(() => createDebouncer(), [])
    
    const [searchInputValue, setSearchInputValue] = useState<any>("")
    const [searchQuery, setSearchQuery] = useState<any>("")
    
    const [combinedResults, setCombinedResults] = useState<any>([])
    const [isResultsVisible, setIsResultsVisible] = useState<boolean>(true)
    const [focusedResultIndex, setFocusedResultIndex] = useState<any>(null)
    
    const [triggerSearch, { isFetching: isSearching }] = useLazyGetSearchResultsQuery()

    const localMapExtent: any = useMemo(() => {
        const currentConfig = getItemWebStorage("currentConfig")
        const rawExtent = currentConfig?.mapDefaults?.extent
        return formatExtent(rawExtent)
    }, [])

    // Debounce input
    useEffect(() => {
        if (focusedResultIndex === -1) {
            const trimmed = searchInputValue.trim()
            debouncer.run(() => setSearchQuery(trimmed), 300)
        } else {
            debouncer.cancel()
        }
    }, [searchInputValue])

    useEffect(() => {
        return () => debouncer.cancel()
    }, [debouncer])

    // Load results
    useEffect(() => {
        if (searchQuery) {
            loadCombinedResults()
            setIsResultsVisible(true)
        } else {
            setCombinedResults([])
        }
    }, [searchQuery])

    async function loadCombinedResults() {
        const geocoderResults = await fetchGeocoderResults()
        const nextResults = [...geocoderResults]

        const pathsList = store.getState().pathsInfo.pathsList
        if (pathsList) {
            const pathMatches = filterPathsByQuery(pathsList, searchQuery)
            nextResults.unshift(...pathMatches)
        }

        const coordinatesResult = parseCoordinatesResult(searchQuery)
        if (coordinatesResult) nextResults.unshift(coordinatesResult)

        setCombinedResults(nextResults)
    }

    async function fetchGeocoderResults() {
        const response = await triggerSearch({ name: searchQuery })
        let results = response?.data || []

        if (localMapExtent) {
            results = results.filter(({ lat, lon }: any) =>
                containsCoordinate(localMapExtent, fromLonLat([lon, lat]))
            )
        }

        return results
    }

    return {
        searchInputValue,
        setSearchInputValue,
        searchQuery,
        setSearchQuery, // exposed for resetting
        combinedResults,
        isResultsVisible,
        setIsResultsVisible,
        focusedResultIndex,
        setFocusedResultIndex,
        isSearching,
        localMapExtent
    }
}

